import { __decorate } from "tslib";
import { AnyDecoder, ArrayDecoder, AutoEncoder, BooleanDecoder, DateDecoder, EnumDecoder, field, IntegerDecoder, StringDecoder } from '@simonbackx/simple-encoding';
import { SimpleErrors } from '@simonbackx/simple-errors';
import { v4 as uuidv4 } from 'uuid';
import { EmailAttachment, Recipient, replaceEmailText, Replacement } from '../endpoints/EmailRequest.js';
import { StamhoofdFilterDecoder } from '../filters/FilteredRequest.js';
import { TinyMember } from '../members/Member.js';
import { MemberDetails } from '../members/MemberDetails.js';
import { MemberWithRegistrationsBlob } from '../members/MemberWithRegistrationsBlob.js';
import { BaseOrganization } from '../Organization.js';
import { User } from '../User.js';
import { EmailTemplate } from './EmailTemplate.js';
export var EmailRecipientFilterType;
(function (EmailRecipientFilterType) {
    EmailRecipientFilterType["RegistrationMembers"] = "RegistrationMembers";
    EmailRecipientFilterType["RegistrationParents"] = "RegistrationParents";
    EmailRecipientFilterType["RegistrationUnverified"] = "RegistrationUnverified";
    // todo: rename to PaymentOther
    EmailRecipientFilterType["Payment"] = "Payment";
    // distinction between payment is useful to be able to not show these in the member portal?
    EmailRecipientFilterType["PaymentOrganization"] = "PaymentOrganization";
    EmailRecipientFilterType["Members"] = "Members";
    EmailRecipientFilterType["MemberParents"] = "MemberParents";
    EmailRecipientFilterType["MemberUnverified"] = "MemberUnverified";
    EmailRecipientFilterType["Orders"] = "Orders";
    EmailRecipientFilterType["ReceivableBalances"] = "ReceivableBalances";
    EmailRecipientFilterType["Documents"] = "Documents";
})(EmailRecipientFilterType || (EmailRecipientFilterType = {}));
export function getExampleRecipient(type = null) {
    return MemberWithRegistrationsBlob.create({
        details: MemberDetails.create({
            firstName: $t(`%13h`),
            lastName: $t(`%13i`),
            email: $t(`%13a`),
        }),
    }).getEmailRecipients(['member'])[0];
}
export var EmailStatus;
(function (EmailStatus) {
    EmailStatus["Draft"] = "Draft";
    /**
     * Queued to be sent any moment now
     */
    EmailStatus["Queued"] = "Queued";
    EmailStatus["Sending"] = "Sending";
    EmailStatus["Sent"] = "Sent";
    EmailStatus["Deleted"] = "Deleted";
    EmailStatus["Failed"] = "Failed";
})(EmailStatus || (EmailStatus = {}));
export var EmailRecipientsStatus;
(function (EmailRecipientsStatus) {
    EmailRecipientsStatus["NotCreated"] = "NotCreated";
    EmailRecipientsStatus["Creating"] = "Creating";
    EmailRecipientsStatus["Created"] = "Created";
})(EmailRecipientsStatus || (EmailRecipientsStatus = {}));
export class EmailRecipientSubfilter extends AutoEncoder {
    constructor() {
        super(...arguments);
        this.type = EmailRecipientFilterType.Members;
        this.filter = null;
        this.search = null;
        /**
         * In case the email is sent to a specific type of relation, we can filter that relation here.
         * E.g. sending an email to organziations -> filter on who to email to for a specific organization (e.g. members with specific role)
         */
        this.subfilter = null;
    }
}
__decorate([
    field({ decoder: new EnumDecoder(EmailRecipientFilterType) })
], EmailRecipientSubfilter.prototype, "type", void 0);
__decorate([
    field({ decoder: StamhoofdFilterDecoder, nullable: true })
], EmailRecipientSubfilter.prototype, "filter", void 0);
__decorate([
    field({ decoder: StringDecoder, nullable: true })
], EmailRecipientSubfilter.prototype, "search", void 0);
__decorate([
    field({ decoder: StamhoofdFilterDecoder, nullable: true, version: 346 })
], EmailRecipientSubfilter.prototype, "subfilter", void 0);
const emailTypesToShowInMemberPortal = new Set([
    EmailRecipientFilterType.Members,
    EmailRecipientFilterType.MemberParents,
    EmailRecipientFilterType.MemberUnverified,
    EmailRecipientFilterType.RegistrationMembers,
    EmailRecipientFilterType.RegistrationParents,
    EmailRecipientFilterType.RegistrationUnverified,
    EmailRecipientFilterType.Documents,
    EmailRecipientFilterType.Payment,
]);
export class EmailRecipientFilter extends AutoEncoder {
    constructor() {
        super(...arguments);
        this.filters = [];
        /**
         * @deprecated.
         * This doesn't do anything and is replaced by automatic email grouping.
         */
        this.groupByEmail = false;
    }
    get canShowInMemberPortal() {
        for (const filter of this.filters) {
            if (emailTypesToShowInMemberPortal.has(filter.type)) {
                return true;
            }
        }
        return false;
    }
}
__decorate([
    field({ decoder: new ArrayDecoder(EmailRecipientSubfilter) })
], EmailRecipientFilter.prototype, "filters", void 0);
__decorate([
    field({ decoder: BooleanDecoder, optional: true })
], EmailRecipientFilter.prototype, "groupByEmail", void 0);
export class Email extends AutoEncoder {
    constructor() {
        super(...arguments);
        this.senderId = null;
        this.organizationId = null;
        this.recipientFilter = EmailRecipientFilter.create({});
        this.subject = null;
        this.status = EmailStatus.Draft;
        this.emailErrors = null;
        this.recipientsStatus = EmailRecipientsStatus.NotCreated;
        this.recipientsErrors = null;
        this.json = {};
        this.text = null;
        this.html = null;
        this.fromAddress = null;
        this.fromName = null;
        this.emailRecipientsCount = null;
        this.otherRecipientsCount = null;
        this.succeededCount = 0;
        this.softFailedCount = 0;
        this.failedCount = 0;
        this.membersCount = 0;
        this.hardBouncesCount = 0;
        this.softBouncesCount = 0;
        this.spamComplaintsCount = 0;
        this.attachments = [];
        this.sentAt = null;
        this.createdAt = new Date();
        this.updatedAt = new Date();
        this.deletedAt = null;
        this.sendAsEmail = true;
        this.showInMemberPortal = false;
    }
    getTemplateType() {
        for (const filter of this.recipientFilter.filters) {
            const d = EmailTemplate.getSavedForRecipient(filter.type);
            if (d) {
                return d;
            }
        }
        return null;
    }
    getSubjectFor(recipient) {
        return replaceEmailText(this.subject || '', (recipient === null || recipient === void 0 ? void 0 : recipient.replacements) || []);
    }
    getSnippetFor(recipient) {
        if (!this.text) {
            return '';
        }
        // Trim starting/ending whitespaces and newline from this.text
        let stripped = this.text.trim();
        // Remove duplicate newlines
        stripped = stripped.replace(/\n+/g, '\n');
        // Remove certain strings:
        // {{greeting}}
        // Dag {{firstName}},
        // Beste,
        const stripStrings = ['{{greeting}}', 'Dag {{firstName}},', 'Beste,', 'Beste {{firstName}},', 'Geachte,', 'Hallo,'];
        if (this.subject) {
            stripStrings.push(this.subject);
        }
        for (const str of stripStrings) {
            if (stripped.startsWith(str)) {
                stripped = stripped.substring(str.length).trim();
            }
        }
        // Remove all (https?://...) links, including the parentheses
        stripped = stripped.replace(/\(https?:\/\/[^\s)]+\)/g, '').trim();
        // Remove all ({{something}}) replacements, including the parentheses - these are often buttons or urls
        stripped = stripped.replace(/\(\{\{[^\s)]+\}\}\)/g, '').trim();
        // Remove all links that are on their own line
        stripped = stripped.replace(/^\s*https?:\/\/[^\s)]+\s*$/gm, '').trim();
        // Remove duplicate spaces
        stripped = stripped.replace(/[ \t]+/g, ' ');
        // Limit to first 2 lines
        const lines = stripped.split('\n').slice(0, 4);
        stripped = lines.join(' ');
        stripped = replaceEmailText(stripped, (recipient === null || recipient === void 0 ? void 0 : recipient.replacements) || []);
        if (stripped.length < 50) {
            // Not worth showing, probably something confusing
            return '';
        }
        return stripped;
    }
}
__decorate([
    field({ decoder: StringDecoder, defaultValue: () => uuidv4() })
], Email.prototype, "id", void 0);
__decorate([
    field({ decoder: StringDecoder, nullable: true, version: 379 })
], Email.prototype, "senderId", void 0);
__decorate([
    field({ decoder: StringDecoder, nullable: true, version: 383 })
], Email.prototype, "organizationId", void 0);
__decorate([
    field({ decoder: EmailRecipientFilter })
], Email.prototype, "recipientFilter", void 0);
__decorate([
    field({ decoder: StringDecoder, nullable: true })
], Email.prototype, "subject", void 0);
__decorate([
    field({ decoder: new EnumDecoder(EmailStatus) })
], Email.prototype, "status", void 0);
__decorate([
    field({ decoder: SimpleErrors, nullable: true, version: 380 })
], Email.prototype, "emailErrors", void 0);
__decorate([
    field({ decoder: new EnumDecoder(EmailRecipientsStatus) })
], Email.prototype, "recipientsStatus", void 0);
__decorate([
    field({ decoder: SimpleErrors, nullable: true, version: 380 })
], Email.prototype, "recipientsErrors", void 0);
__decorate([
    field({ decoder: AnyDecoder })
], Email.prototype, "json", void 0);
__decorate([
    field({ decoder: StringDecoder, nullable: true })
], Email.prototype, "text", void 0);
__decorate([
    field({ decoder: StringDecoder, nullable: true })
], Email.prototype, "html", void 0);
__decorate([
    field({ decoder: StringDecoder, nullable: true })
], Email.prototype, "fromAddress", void 0);
__decorate([
    field({ decoder: StringDecoder, nullable: true })
], Email.prototype, "fromName", void 0);
__decorate([
    field({ decoder: IntegerDecoder, nullable: true, field: 'recipientCount' }),
    field({ decoder: IntegerDecoder, nullable: true, version: 380, field: 'emailRecipientsCount' })
], Email.prototype, "emailRecipientsCount", void 0);
__decorate([
    field({ decoder: IntegerDecoder, nullable: true, version: 380 })
], Email.prototype, "otherRecipientsCount", void 0);
__decorate([
    field({ decoder: IntegerDecoder, version: 380 })
], Email.prototype, "succeededCount", void 0);
__decorate([
    field({ decoder: IntegerDecoder, version: 380 })
], Email.prototype, "softFailedCount", void 0);
__decorate([
    field({ decoder: IntegerDecoder, version: 380 })
], Email.prototype, "failedCount", void 0);
__decorate([
    field({ decoder: IntegerDecoder, version: 380 })
], Email.prototype, "membersCount", void 0);
__decorate([
    field({ decoder: IntegerDecoder, nullable: true, version: 381 })
], Email.prototype, "hardBouncesCount", void 0);
__decorate([
    field({ decoder: IntegerDecoder, nullable: true, version: 381 })
], Email.prototype, "softBouncesCount", void 0);
__decorate([
    field({ decoder: IntegerDecoder, nullable: true, version: 381 })
], Email.prototype, "spamComplaintsCount", void 0);
__decorate([
    field({ decoder: new ArrayDecoder(EmailAttachment) })
], Email.prototype, "attachments", void 0);
__decorate([
    field({ decoder: DateDecoder, nullable: true })
], Email.prototype, "sentAt", void 0);
__decorate([
    field({ decoder: DateDecoder })
], Email.prototype, "createdAt", void 0);
__decorate([
    field({ decoder: DateDecoder })
], Email.prototype, "updatedAt", void 0);
__decorate([
    field({ decoder: DateDecoder, nullable: true, version: 382 })
], Email.prototype, "deletedAt", void 0);
__decorate([
    field({ decoder: BooleanDecoder, version: 384 })
], Email.prototype, "sendAsEmail", void 0);
__decorate([
    field({ decoder: BooleanDecoder, version: 384 })
], Email.prototype, "showInMemberPortal", void 0);
export class EmailRecipient extends AutoEncoder {
    constructor() {
        super(...arguments);
        this.emailId = '';
        this.objectId = null;
        this.emailType = null;
        this.firstName = null;
        this.lastName = null;
        this.email = null;
        this.memberId = null;
        this.member = null;
        this.userId = null;
        this.replacements = [];
        this.failErrorMessage = null;
        this.failCount = 0;
        this.failError = null;
        this.previousFailError = null;
        this.hardBounceError = null;
        this.softBounceError = null;
        this.spamComplaintError = null;
        this.firstFailedAt = null;
        this.lastFailedAt = null;
        this.sentAt = null;
        this.createdAt = new Date();
        this.updatedAt = new Date();
    }
    get name() {
        if (!this.firstName) {
            return this.lastName;
        }
        if (!this.lastName) {
            return this.firstName;
        }
        return this.firstName + ' ' + this.lastName;
    }
    getDefaultReplacements() {
        var _a;
        return Recipient.create({
            ...this,
            email: (_a = this.email) !== null && _a !== void 0 ? _a : '',
        }).getDefaultReplacements();
    }
    getReplacements(organization, platform) {
        return [
            ...this.replacements,
            ...this.getDefaultReplacements(),
            ...(organization ? organization.meta.getEmailReplacements(organization) : []),
            ...platform.config.getEmailReplacements(platform),
        ];
    }
    getRecipient() {
        var _a;
        return Recipient.create({
            ...this,
            email: (_a = this.email) !== null && _a !== void 0 ? _a : '',
        });
    }
}
__decorate([
    field({ decoder: StringDecoder, defaultValue: () => uuidv4() })
], EmailRecipient.prototype, "id", void 0);
__decorate([
    field({ decoder: StringDecoder })
], EmailRecipient.prototype, "emailId", void 0);
__decorate([
    field({ decoder: StringDecoder, nullable: true, version: 355 })
], EmailRecipient.prototype, "objectId", void 0);
__decorate([
    field({ decoder: StringDecoder, nullable: true, version: 355 })
], EmailRecipient.prototype, "emailType", void 0);
__decorate([
    field({ decoder: StringDecoder, nullable: true })
], EmailRecipient.prototype, "firstName", void 0);
__decorate([
    field({ decoder: StringDecoder, nullable: true })
], EmailRecipient.prototype, "lastName", void 0);
__decorate([
    field({ decoder: StringDecoder }),
    field({
        decoder: StringDecoder,
        nullable: true,
        version: 380,
        downgrade: function (newer) {
            return newer !== null && newer !== void 0 ? newer : '';
        },
    })
], EmailRecipient.prototype, "email", void 0);
__decorate([
    field({ decoder: StringDecoder, nullable: true, version: 380 })
], EmailRecipient.prototype, "memberId", void 0);
__decorate([
    field({ decoder: TinyMember, nullable: true, version: 383 })
], EmailRecipient.prototype, "member", void 0);
__decorate([
    field({ decoder: StringDecoder, nullable: true, version: 380 })
], EmailRecipient.prototype, "userId", void 0);
__decorate([
    field({ decoder: new ArrayDecoder(Replacement) })
], EmailRecipient.prototype, "replacements", void 0);
__decorate([
    field({ decoder: StringDecoder, nullable: true })
], EmailRecipient.prototype, "failErrorMessage", void 0);
__decorate([
    field({ decoder: IntegerDecoder })
], EmailRecipient.prototype, "failCount", void 0);
__decorate([
    field({ decoder: SimpleErrors, nullable: true, version: 380 })
], EmailRecipient.prototype, "failError", void 0);
__decorate([
    field({ decoder: SimpleErrors, nullable: true, version: 385 })
], EmailRecipient.prototype, "previousFailError", void 0);
__decorate([
    field({ decoder: StringDecoder, nullable: true, version: 380 })
], EmailRecipient.prototype, "hardBounceError", void 0);
__decorate([
    field({ decoder: StringDecoder, nullable: true, version: 380 })
], EmailRecipient.prototype, "softBounceError", void 0);
__decorate([
    field({ decoder: StringDecoder, nullable: true, version: 380 })
], EmailRecipient.prototype, "spamComplaintError", void 0);
__decorate([
    field({ decoder: DateDecoder, nullable: true })
], EmailRecipient.prototype, "firstFailedAt", void 0);
__decorate([
    field({ decoder: DateDecoder, nullable: true })
], EmailRecipient.prototype, "lastFailedAt", void 0);
__decorate([
    field({ decoder: DateDecoder, nullable: true })
], EmailRecipient.prototype, "sentAt", void 0);
__decorate([
    field({ decoder: DateDecoder })
], EmailRecipient.prototype, "createdAt", void 0);
__decorate([
    field({ decoder: DateDecoder })
], EmailRecipient.prototype, "updatedAt", void 0);
export function isSoftEmailRecipientError(error) {
    if (error.hasCode('email_skipped_unsubscribed')) {
        return true;
    }
    if (error.hasCode('email_skipped_duplicate_recipient')) {
        return true;
    }
    return false;
}
export function bounceErrorToHuman(message) {
    message = message.toLowerCase();
    if (message.startsWith('smtp; 554 4.4.7') || message.includes('storage') || message.includes('quota') || message.match(/inbox.*full/)) {
        return $t('%1GI');
    }
    if (message.includes('failed to establish connection') || message.includes('connection timed out')) {
        return $t('%1GJ');
    }
    if (message.includes('unable to lookup dns')) {
        return $t('%1GK');
    }
    if (message.includes('hop count exceeded')) {
        return $t('%1GL');
    }
    if (message.includes('recipient address rejected: access denied') || message.includes('user does not exist') || message.includes('user unknown') || message.includes('the email account that you tried to reach does not exist')) {
        return $t('%1GM');
    }
}
export class EmailPreview extends Email {
    constructor() {
        super(...arguments);
        this.exampleRecipient = null;
        this.user = null;
        this.organization = null;
    }
    get replacedSubject() {
        return this.getSubjectFor(this.exampleRecipient);
    }
    get snippet() {
        return this.getSnippetFor(this.exampleRecipient);
    }
}
__decorate([
    field({ decoder: EmailRecipient, nullable: true })
], EmailPreview.prototype, "exampleRecipient", void 0);
__decorate([
    field({ decoder: User, nullable: true, version: 383 })
], EmailPreview.prototype, "user", void 0);
__decorate([
    field({ decoder: BaseOrganization, nullable: true, version: 383 })
], EmailPreview.prototype, "organization", void 0);
export class EmailWithRecipients extends Email {
    constructor() {
        super(...arguments);
        this.recipients = [];
        this.organization = null;
    }
    get exampleRecipient() {
        var _a;
        return (_a = this.recipients[0]) !== null && _a !== void 0 ? _a : null;
    }
    get replacedSubject() {
        return this.getSubjectFor(this.exampleRecipient);
    }
    get snippet() {
        return this.getSnippetFor(this.exampleRecipient);
    }
}
__decorate([
    field({ decoder: new ArrayDecoder(EmailRecipient) })
], EmailWithRecipients.prototype, "recipients", void 0);
__decorate([
    field({ decoder: BaseOrganization, nullable: true, version: 383 })
], EmailWithRecipients.prototype, "organization", void 0);
//# sourceMappingURL=Email.js.map